import * as z from 'zod'

import { CreateLinkageApiKeyVars, UpdateLinkageApiKeyVars } from '@/common/api/params/linkageApiKey'
import { NameLabelMapping } from '@/common/app/message'
import { StringLength } from '@/common/form/schema'
import { LinkageApiKey } from '@/common/model/linkageApiKey'

// 単一IP（203.0.113.10）または CIDR 範囲（198.51.100.0/24）。IPv4 / IPv6 の両方を許容する。
const IPV4 = String.raw`(?:(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.){3}(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)`
const IPV6 = String.raw`(?:[0-9A-Fa-f]{0,4}:){2,7}[0-9A-Fa-f]{0,4}`
const IP_OR_CIDR = new RegExp(
  `^(?:${IPV4}(?:/(?:3[0-2]|[12]?\\d))?|${IPV6}(?:/(?:12[0-8]|1[01]\\d|\\d{1,2}))?)$`
)

export const IP_FORMAT_MESSAGE =
  'IPアドレスまたはCIDR（例: 203.0.113.10 / 198.51.100.0/24）で入力してください'

// 連携APIキー。入力項目は用途と送信元IP制限のみで、キーの値はサーバーが自動生成する（編集も不可）。
// allowed_ips は「入力があれば下に空欄が増える」UIのため、末尾の空欄を許す構造にしている。
// 空欄は送信時に除去し、すべて空なら「制限なし（全IP許可）」となる。
export const linkageApiKeySchema = z.object({
  id: z.number().readonly().optional(),
  purpose: z.string().min(1).max(StringLength.NORMAL_MAX),
  allowed_ips: z.array(
    z.object({
      value: z.string().refine((v) => v.trim() === '' || IP_OR_CIDR.test(v.trim()), {
        message: IP_FORMAT_MESSAGE
      })
    })
  )
})

export type LinkageApiKeyFormSchemaType = z.infer<typeof linkageApiKeySchema>

/** 入力欄の配列 → 送信用の文字列配列（空欄を除去） */
const toIpList = (rows: { value: string }[]): string[] =>
  rows.map((r) => r.value.trim()).filter((v) => v !== '')

/** 文字列配列 → 入力欄の配列（末尾に空欄を1つ足して追加入力できるようにする） */
export const toIpRows = (ips: string[]): { value: string }[] => [
  ...ips.map((value) => ({ value })),
  { value: '' }
]

export const convertLinkageApiKeySchemaToVars = (
  schema: LinkageApiKeyFormSchemaType
): CreateLinkageApiKeyVars | UpdateLinkageApiKeyVars => {
  if (schema.id !== undefined) {
    return {
      linkage_api_key_id: schema.id,
      purpose: schema.purpose,
      allowed_ips: toIpList(schema.allowed_ips)
    }
  }
  return {
    purpose: schema.purpose,
    allowed_ips: toIpList(schema.allowed_ips)
  }
}

export const convertLinkageApiKeyToSchema = (
  linkageApiKey: LinkageApiKey
): LinkageApiKeyFormSchemaType => {
  return {
    id: linkageApiKey.id,
    purpose: linkageApiKey.purpose,
    allowed_ips: toIpRows(linkageApiKey.allowed_ips ?? [])
  }
}

export const DEFAULT_LINKAGE_API_KEY_SCHEMA: LinkageApiKeyFormSchemaType = {
  purpose: '',
  allowed_ips: [{ value: '' }]
}

export const linkageApiKeyNameLabelMapping: NameLabelMapping = {
  purpose: '用途',
  allowed_ips: '許可する送信元IP'
}
